import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import {
    DEFAULT_CULTURE_CODE,
    DEFAULT_LANGUAGE_CODE,
    FULL_SUPPORT_LANGUAGES,
    PLATFORM_ADMIN_ENTERPRISE_ID,
} from '@mews-commander/constants';
import { BusinessError } from '@mews/framework';
import { createUUID } from '@mews/std';
import { isLoggedInSession, LoggedInSession, Session } from './types';

type SetCurrentSessionParams = Omit<LoggedInSession, 'cultureCode' | 'languageCode' | 'sessionId'> &
    Partial<Pick<LoggedInSession, 'cultureCode' | 'languageCode'>>;

interface SessionStore {
    currentSession: Session;
    changeSessionLanguage: (languageCode: string) => void;
    setCurrentSession: (session: SetCurrentSessionParams) => void;
    updateCurrentSession: (session: Partial<Session>) => void;
}

function getDefaultLanguage() {
    const userLanguage = navigator.language || DEFAULT_LANGUAGE_CODE;
    const languageCode = userLanguage.split('-')[0];
    return FULL_SUPPORT_LANGUAGES.find((lang) => lang.startsWith(languageCode)) || DEFAULT_LANGUAGE_CODE;
}

const sessionStore = createStore<SessionStore>((set) => ({
    currentSession: {
        cultureCode: DEFAULT_CULTURE_CODE,
        languageCode: getDefaultLanguage(),
        sessionId: createUUID(),
    },
    changeSessionLanguage: (languageCode) =>
        set((state) => ({ currentSession: { ...state.currentSession, languageCode } })),
    setCurrentSession: (session) =>
        set((state) => ({
            currentSession: { ...state.currentSession, ...session },
        })),
    updateCurrentSession: (session) =>
        set((state) => ({
            currentSession: { ...state.currentSession, ...session },
        })),
}));

export const { setCurrentSession, updateCurrentSession, changeSessionLanguage } = sessionStore.getState();

export const useCurrentSession = () => useStore(sessionStore, (store) => store.currentSession);

export const useCurrentLoggedInSession = () => {
    const session = useCurrentSession();

    if (!isLoggedInSession(session)) {
        throw new BusinessError(`Session is not LoggedInSession`);
    }

    return session;
};

export const useCultureCode = () => useCurrentSession().cultureCode;

export const useCurrentEnterpriseId = () => useCurrentLoggedInSession().currentEnterpriseId as string;

/**
 * A strict variant of `useCurrentEnterpriseId` that always returns a GUID
 * or a GUID-like result for a platform admin (instead of `null`)
 *
 * Used by routing to construct a valid URL with `enterpriseId` in it.
 */
export const useStrictCurrentEnterpriseId = () => {
    const enterpriseId = useCurrentEnterpriseId();
    return enterpriseId === null ? PLATFORM_ADMIN_ENTERPRISE_ID : enterpriseId;
};

export const useCurrentProfileId = () => useCurrentLoggedInSession().currentProfileId;

export const useLanguageCode = () => useCurrentSession().languageCode;

export const useSessionId = () => {
    const { sessionId } = useCurrentSession();

    if (sessionId === undefined) {
        throw new BusinessError(`sessionId is undefined`);
    }
    return sessionId;
};

/** Only use in special cases outside of react, e.g. while migrating legacy redux selectors */
const getCurrentSession = () => sessionStore.getState().currentSession;

const getCurrentLoggedInSession = () => {
    const session = getCurrentSession();

    if (!isLoggedInSession(session)) {
        throw new BusinessError(`Session is not LoggedInSession`);
    }

    return session;
};

/**
 * @deprecated Use only in special cases outside of react, e.g. in beforeLoad of a route
 */
export const isCurrentSessionLoggedIn = () => isLoggedInSession(getCurrentSession());

/**
 * @deprecated use useCultureCode instead
 * Only use in special cases outside of react, e.g. while migrating legacy redux selectors.
 * */
export const getCultureCode = () => getCurrentSession().cultureCode;

/**
 * @deprecated use useCurrentEnterpriseId instead
 * Only use in special cases outside of react, e.g. while migrating legacy redux selectors.
 * */
export const getCurrentEnterpriseId = () => {
    const session = getCurrentSession();

    // To be refactored part of OPS-15311
    // @ts-expect-error this function is called before you are logged throwing error when using getCurrentLoggedInSession
    return session.currentEnterpriseId as string;
};

/**
 * A strict variant of `getCurrentEnterpriseId` that always returns a GUID
 * or a GUID-like result for a platform admin (instead of `null`)
 *
 * Used by routing to construct a valid URL with `enterpriseId` in it.
 */
export const getStrictCurrentEnterpriseId = () => {
    const enterpriseId = getCurrentEnterpriseId();
    return enterpriseId === null ? PLATFORM_ADMIN_ENTERPRISE_ID : enterpriseId;
};

/**
 * @deprecated use useCurrentProfileId instead
 * Only use in special cases outside of react, e.g. while migrating legacy redux selectors.
 * */
export const getCurrentProfileId = () => getCurrentLoggedInSession().currentProfileId;

/**
 * @deprecated use useCurrentProfileId instead
 * This function was created temporally because its called when the User is not logged in.
 * We should not be accessing the currentProfileId when we are not logged in.
 * */
export const getCurrentProfileIdOrNull = () => {
    const session = getCurrentSession();

    if (isLoggedInSession(session)) {
        return session.currentProfileId;
    }

    return null;
};

/**
 * @deprecated use useLanguageCode instead
 * Only use in special cases outside of react, e.g. while migrating legacy redux selectors.
 * */
export const getLanguageCode = () => getCurrentSession().languageCode;

/**
 * @deprecated use useSessionId instead
 * Only use in special cases outside of react, e.g. while migrating legacy redux selectors.
 * */
export const getSessionId = () => getCurrentSession().sessionId;
